/**
 * DICOM → JPEG for the screening pipeline (Cornerstone + WADO image loader).
 */

import cornerstone from "cornerstone-core";
import * as cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import * as dicomParser from "dicom-parser";
import { isIOSSafari } from './deviceDetection';

let _inited = false;

/** Browser JPEG quality (main path + embedded-JPEG fallback). */
const DICOM_JPEG_QUALITY = 0.95;

/**
 * Maximum long-edge pixel count before capping the DICOM decode canvas.
 * Keeps peak RAM manageable on iOS Safari where a full-resolution canvas
 * decode can push the tab past WebKit's memory limit before inference starts.
 */
const DICOM_IOS_MAX_LONG_EDGE = 1280;
const DICOM_DESKTOP_MAX_LONG_EDGE = 2048;

/** Return pixel dimensions capped to the platform's long-edge limit. */
function capDicomDimensions(w, h) {
  const maxLong = isIOSSafari() ? DICOM_IOS_MAX_LONG_EDGE : DICOM_DESKTOP_MAX_LONG_EDGE;
  const long = Math.max(w, h);
  if (long <= maxLong) return { w, h };
  const scale = maxLong / long;
  return {
    w: Math.max(1, Math.round(w * scale)),
    h: Math.max(1, Math.round(h * scale)),
  };
}

export function formatDicomConversionError(err) {
  const msg = err?.message || String(err);
  if (/decodeTask/i.test(msg) && /undefined/i.test(msg)) {
    return "DICOM decoder failed to start. Reload the page and try again.";
  }
  if (/timed out/i.test(msg)) {
    return "Reading this file took too long. It may be invalid or unsupported DICOM.";
  }
  return msg;
}

/** Avoid hung UI when Cornerstone/worker never resolves (bad or huge DICOM). */
const DICOM_LOAD_TIMEOUT_MS = 45_000;

function loadCornerstoneImage(imageId) {
  const loadPromise = cornerstone.loadImage(imageId);
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(
      () => reject(new Error("DICOM load timed out")),
      DICOM_LOAD_TIMEOUT_MS
    );
  });
  return Promise.race([loadPromise, timeoutPromise]);
}

function initCornerstoneOnce() {
  if (_inited) return;
  try {
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    // Must include taskConfiguration.decodeTask — worker init calls
    // initialize(config.taskConfiguration) and reads .decodeTask (library default).
    cornerstoneWADOImageLoader.webWorkerManager.initialize({
      maxWebWorkers: 1,
      startWebWorkersOnDemand: true,
      taskConfiguration: {
        decodeTask: {
          initializeCodecsOnStartup: false,
          usePDFJS: false,
          strict: false,
        },
      },
    });
    _inited = true;
  } catch (e) {
    throw e;
  }
}

/**
 * Filename / MIME only — does not read file bytes (for FilePond type hints and accept rules).
 */
export function isDicomExtension(file) {
  if (!file || typeof file.name !== "string") return false;
  const n = file.name.toLowerCase();
  return (
    n.endsWith(".dcm") ||
    n.endsWith(".dic") ||
    file.type === "application/dicom"
  );
}

/** PS3.10: "DICM" at bytes 128–131 after preamble (4-byte read). */
async function readHasPart10DicmAt128(file) {
  if (!file || typeof file.slice !== "function") return false;
  if (file.size < 132) return false;
  const buf = await file.slice(128, 132).arrayBuffer();
  const b = new Uint8Array(buf);
  return (
    b[0] === 0x44 &&
    b[1] === 0x49 &&
    b[2] === 0x43 &&
    b[3] === 0x4d
  );
}

/**
 * Run DICOM conversion when the name looks like DICOM or the file has a Part 10 DICM signature.
 */
export async function isDicomCandidate(file) {
  if (isDicomExtension(file)) return true;
  try {
    return await readHasPart10DicmAt128(file);
  } catch {
    return false;
  }
}

function baseName(name) {
  return (name || "fundus").replace(/\.[^.]+$/i, "");
}

function findJpegSoi(u8) {
  const len = u8.length;
  for (let i = 0; i < len - 1; i++) {
    if (u8[i] === 0xff && u8[i + 1] === 0xd8) return i;
  }
  return -1;
}

/** After Cornerstone fails: first embedded JPEG (e.g. raw JPEG bytes with a `.dcm` name). */
async function extractEmbeddedJpegAsImageFile(file) {
  const u8 = new Uint8Array(await file.arrayBuffer());
  const start = findJpegSoi(u8);
  if (start < 0) throw new Error("No JPEG SOI in file");
  let end = u8.length;
  for (let i = u8.length - 2; i >= start; i--) {
    if (u8[i] === 0xff && u8[i + 1] === 0xd9) {
      end = i + 2;
      break;
    }
  }
  const slice = u8.subarray(start, end);
  const bmp = await createImageBitmap(new Blob([slice], { type: "image/jpeg" }));
  try {
    const { w: cw, h: ch } = capDicomDimensions(bmp.width, bmp.height);
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bmp, 0, 0, cw, ch);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
        "image/jpeg",
        DICOM_JPEG_QUALITY
      );
    });
    canvas.width = 0;
    canvas.height = 0;
    return new File([blob], `${baseName(file.name)}.jpeg`, { type: "image/jpeg" });
  } finally {
    bmp.close();
  }
}

/**
 * Convert a DICOM file to a JPEG File (same shape as camera uploads).
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function convertDicomToImageFile(file) {
  try {
    initCornerstoneOnce();
  } catch (e) {
    throw new Error(`DICOM init failed: ${formatDicomConversionError(e)}`);
  }

  const blobUrl = URL.createObjectURL(file);
  const imageId = `wadouri:${blobUrl}`;

  try {
    const image = await loadCornerstoneImage(imageId);
    const { w, h } = capDicomDimensions(image.width, image.height);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    cornerstone.renderToCanvas(canvas, image);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
        "image/jpeg",
        DICOM_JPEG_QUALITY
      );
    });
    canvas.width = 0;
    canvas.height = 0;
    return new File([blob], `${baseName(file.name)}.jpeg`, { type: "image/jpeg" });
  } catch (e) {
    try {
      return await extractEmbeddedJpegAsImageFile(file);
    } catch {
      throw new Error(`Conversion failed: ${formatDicomConversionError(e)}`);
    }
  } finally {
    URL.revokeObjectURL(blobUrl);
    try {
      cornerstone.imageCache.removeImageLoadObject(imageId);
    } catch (_) {}
  }
}
